import {
    ACTIVITY_PRICE_TYPE_PRICE_RANGE,
    type ActivityPriceType,
    ACTIVITY_PRICE_TYPE_PRICE_SCALE,
    ACTIVITY_PRICE_TYPE_PER_PARTICIPANT,
} from '@funkey/constants';
import type { PriceRule } from '@funkey/types/graphql/graphql';
import assert from 'assert';
import { Filters } from '../../utils';

/** We can't import `apps/api` types here, so we added generic options so that both Graphql & DB variants are supported. */
interface Money {
    toUnit(): number;
}

function isMoney(value: unknown): value is Money {
    return (
        typeof value === 'object' && value !== null && typeof (value as any).toUnit === 'function'
    );
}

type Variant = {
    extraCost?: number | Money | null;
    extraCostPerCount?: number | null;
    priceRules?: (Omit<PriceRule, '__typename' | 'variantId'> | null)[] | null;
};

export type ExtendedPriceRule = Omit<PriceRule, '__typename'> & {
    /** This value is not stored in our db, but it's easier to update/delete pricerules having a unique key */
    id: string;
    /** This value is not stored in our db, but is used to calculate the price */
    minParticipants: number;
    price: number;
    extraPrice?: number;
};

export const calculatePrice = ({
    participants,
    variant,
    priceType,
}: {
    participants: number;
    variant: Variant;
    priceType: ActivityPriceType;
}) => {
    const extraCost =
        (typeof variant.extraCost === 'number'
            ? variant.extraCost
            : isMoney(variant.extraCost)
              ? variant.extraCost.toUnit()
              : 0) ?? 0;
    const extraCostPerCount = variant.extraCostPerCount ?? 0;

    const participantCount = getMinimumParticipantCount({
        participants,
        variant,
    });

    // 1. Starting cost
    let totalCost = 0;

    // 2. Extra cost for participants
    const setsOfParticipants = extraCostPerCount
        ? Math.ceil(participantCount / extraCostPerCount)
        : 0;

    const subTotal = setsOfParticipants ? setsOfParticipants * extraCost : 0;

    totalCost += subTotal;

    // 3. Extra cost for price rules according to type
    if (variant.priceRules?.length) {
        const priceCalculations = {
            [ACTIVITY_PRICE_TYPE_PER_PARTICIPANT]: calculatePricePerParticipant,
            [ACTIVITY_PRICE_TYPE_PRICE_RANGE]: calculatePriceForRange,
            [ACTIVITY_PRICE_TYPE_PRICE_SCALE]: calculatePriceForScale,
        };

        const calculatePriceByType =
            priceCalculations[priceType ?? ACTIVITY_PRICE_TYPE_PER_PARTICIPANT];

        const subTotal = calculatePriceByType({
            priceRules: variant.priceRules.filter(Filters.nonnull) as any,
            participantCount,
        });

        totalCost += subTotal;
    }

    // If we don't want that users go below the minimum participant count, we should use participantCount here
    return {
        participants,
        price: totalCost,
        pricePerParticipant: Math.round((totalCost / participants) * 100) / 100,
    };
};

/** Cheapest and dearest of a set of variant prices, skipping 0 ("on request", not free); null when none is priced. */
export function variantPriceSpan(prices: ReadonlyArray<number>) {
    const priced = prices.filter(price => Number.isFinite(price) && price > 0);
    return priced.length > 0 ? { min: Math.min(...priced), max: Math.max(...priced) } : null;
}

export function transformPriceRules(
    priceRules: (ExtendedPriceRule & { id: string })[],
): ExtendedPriceRule[] {
    priceRules.sort((a, b) => a.participants - b.participants);
    const priceRulesWithMinParticipants = priceRules.map((priceRule, index) => {
        const minParticipants = index === 0 ? 1 : (priceRules[index - 1]?.participants ?? 0) + 1;
        const price = isMoney(priceRule.price) ? priceRule.price.toUnit() : priceRule.price;
        const extraPrice = isMoney(priceRule.extraPrice)
            ? priceRule.extraPrice.toUnit()
            : priceRule.extraPrice;

        return {
            ...priceRule,
            price,
            extraPrice,
            minParticipants,
        };
    });

    return priceRulesWithMinParticipants;
}

export const getFirstTieredPricingRule = ({ variant }: { variant: Variant }) => {
    const { priceRules } = variant;
    if (!priceRules?.length) return null;

    const firstTieredPricingRule = priceRules.reduce((prev, curr) => {
        if (!curr || !prev) return curr || prev;
        return curr.participants < prev.participants ? curr : prev;
    });

    return firstTieredPricingRule;
};

/** This function returns the minimum participant count for the variant.
 * The minimum participant count participant count of the first price rule.
 * If the requested participant count is lower than the first price rule, the participant count of the first price rule is returned.
 */
export const getMinimumParticipantCount = ({
    participants,
    variant,
}: {
    participants: number;
    variant: Variant;
}) => {
    const { priceRules } = variant;
    if (!priceRules?.length) return participants;

    const firstTieredPricingRule = getFirstTieredPricingRule({ variant });
    const minParticipants = firstTieredPricingRule?.participants;

    return minParticipants && participants < minParticipants ? minParticipants : participants;
};

/** This price calculation is based on a price per participant.
 * This checks calculation checks the most accurate price rule for the requested participant count.
 * Fixed costs (`extraPrice`) is possible here. If the appliccable price rule has a fixed cost, it will be added to the total price.
 *  ## Example:
 * ```
 * {
    priceRules: [
        { participants: 10, price: 25 }, // 10 * 25 = 250 | rest 0 participants
        { participants: 15, price: 20 },
        { participants: 20, price: 18 }, // 20 * 18 | rest 10 participants
        { participants: 40, price: 15 }, // 10 * 15 | rest 1 participant
        { participants: 50, price: 14, extraPrice: 100 }, // 1 * 14 | rest 0 participants
    ],
    extraCost: 5,
    extraCostPerCount: 10,
    }
 * ```
 * If the requested participants is `50`
 * - **costs**: (50*14) = 700
 * - **extra cost**: 50/10 * 5 = 25
 * - **fixed costs**: 100 
 * - **total cost**: 825 
 */
const calculatePricePerParticipant = ({
    priceRules,
    participantCount,
}: {
    priceRules: ExtendedPriceRule[];
    participantCount: number;
}) => {
    const applicablePriceRules = priceRules.filter(rule => rule.participants <= participantCount);

    if (!applicablePriceRules.length) return 0;
    const rules = transformPriceRules(applicablePriceRules);

    const closestPriceRule = rules.reduce((prev, curr) =>
        curr.participants > prev.participants ? curr : prev,
    );

    return closestPriceRule.price * participantCount + (closestPriceRule?.extraPrice || 0);
};

/** This price calculation is based on a price range
 * This checks in what range the participant count is, and calculates the price based on the price rules in that range
 * Fixed costs (`extraPrice`) is not possible here, since we are already working with a total price per range
 *  ## Example:
 * ```
 * {
    priceRules: [
        { participants: 10, price: 2000 },
        { participants: 15, price: 2750 }, 
        { participants: 20, price: 3000 }, 
        { participants: 40, price: 5000 }, 
    ],
    extraCost: 5,
    extraCostPerCount: 10,
    }
 * ```
 * If the requested participants is `50`
 * - **costs**: 5000
 * - **extra cost**: 50/10 * 5 = 25
 * - **total cost**: 5025
 */
const calculatePriceForRange = ({
    priceRules,
    participantCount,
}: {
    priceRules: ExtendedPriceRule[];
    participantCount: number;
}) => {
    const sortedRules = [...priceRules].sort((a, b) => a.participants - b.participants);
    const rules = transformPriceRules(sortedRules);

    let applicablePriceRule: ExtendedPriceRule | undefined;
    for (const rule of rules) {
        if (rule.participants <= participantCount) {
            applicablePriceRule = rule;
        } else {
            break;
        }
    }

    return applicablePriceRule
        ? applicablePriceRule.price + (applicablePriceRule.extraPrice ?? 0)
        : 0;
};

/** This price calculation is based on price scales.
 * The amount of requested participants is sliced in scales according to the price rules.
 * The first price rule dictates the minimum participants the customer has to pay for.
 * This price type allows fixed costs (`extraPrice`) to be added to the total price of the last scale the particpants are in.
 * 
 *  ## Example:
 * ```
 * {
    priceRules: [
        { participants: 10, price: 25 }, // 14 * 25 | rest: 36 (p0 to p14)
        { participants: 15, price: 20 }, // 5 * 20 | rest: 31 (p15 to p19)
        { participants: 20, price: 18 }, // 20 * 18 | rest: 11 (p20 to p39)
        { participants: 40, price: 15 }, // 10 * 15 | rest: 1 (p40 to p49)
        { participants: 50, price: 14, extraPrice: 100 }, // 1 * 14 | rest: 0 (p50)
    ],
    extraCost: 5,
    extraCostPerCount: 10,
    }
 * ```
 * If the requested participants is 50
 * - **costs**: (14 * 25) + (5 * 20) + (20 * 18) + (10 * 15) + (1 * 14) = 974
 * - **extra cost**: 50/10 * 5 = 25
 * - **fixed costs**: 100 (called extraPrice here)
 * - **total cost**: 1099
 */
const calculatePriceForScale = ({
    priceRules,
    participantCount,
}: {
    priceRules: ExtendedPriceRule[];
    participantCount: number;
}) => {
    let totalPrice = 0;
    let remainingParticipants = participantCount;
    const rules = transformPriceRules(priceRules).sort((a, b) => a.participants - b.participants);

    // If there is only one rule, return the price for the participant count
    // If the requested participants are less than the first rule, use the first rule's participant count
    if (rules.length === 1) {
        const rule = rules[0];
        assert(rule);
        return Math.max(participantCount, rule.participants) * rule.price + (rule.extraPrice ?? 0);
    }

    // This rule will be used to get the extra costs and calculate the remaining participants
    let lastApplicableRule;

    for (let i = 0; i < rules.length; i++) {
        const currentRule = rules[i];
        assert(currentRule);

        lastApplicableRule = currentRule;

        // We need to check the next rule to get our max boundary
        const nextRule = rules[i + 1];
        if (!nextRule) break;

        // Get the participant boundaries of this scale. We exclude the first particpant from the next scale, because the next scale starts at that number
        const max = Math.min(participantCount, nextRule.participants - 1);
        const min = Math.min(i === 0 ? 0 : currentRule.participants, max);

        // Calculate the number of participants in this scale
        const participantsInThisRange = max - min + (i ? 1 : 0);
        totalPrice += Math.min(participantsInThisRange, remainingParticipants) * currentRule.price;

        // Subtract these participants from the remaining count
        if (remainingParticipants > participantsInThisRange) {
            remainingParticipants -= participantsInThisRange;
        } else {
            remainingParticipants = 0;
            break;
        }
    }

    // Once we looped through all rules, we can now assert than we have an applicable rule
    assert(lastApplicableRule);

    return (
        totalPrice +
        remainingParticipants * lastApplicableRule.price +
        (lastApplicableRule.extraPrice ?? 0)
    );
};

export default calculatePrice;
